import * as React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAuth } from '~/contexts/AuthContext';
import { AppDispatch } from '~/store';
import { setToast } from '~/store/features/toast';
import AppTheme from '~/components/shared-theme/AppTheme';
import { SignInCard } from '~/pages/sign-in/components/SignInCard';
import { SignInContainer } from '~/pages/sign-in/components/SignInContainer';
import ForgotPassword from './components/ForgotPassword';
import Divider from '@mui/material/Divider';
import {
  FacebookIcon,
  GoogleIcon,
  SitemarkIcon,
} from './components/CustomIcons';

function SignIn(props: { disableCustomTheme?: boolean }) {
  const { login, loginError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [openForgotPassword, setOpenForgotPassword] = React.useState(false);

  // Yup validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required'),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async values => {
      const success = await login(values.email, values.password);
      if (success) {
        const { from } = location.state || { from: { pathname: '/' } };
        navigate(from, { replace: true });
      }
    },
  });

  React.useEffect(() => {
    if (loginError) {
      dispatch(
        setToast({ message: loginError, severity: 'error', duration: 3000 })
      );
    }
  }, [dispatch, loginError]);

  const handleClickOpen = () => {
    setOpenForgotPassword(true);
  };

  const handleClose = () => {
    setOpenForgotPassword(false);
  };

  // @ts-ignore
  const useSocialLogins = false;

  const SocialLogins = useSocialLogins && (
    <>
      <Divider>or</Divider>
      <Button
        fullWidth
        variant="outlined"
        onClick={() => alert('Sign in with Google')}
        startIcon={<GoogleIcon />}
      >
        Sign in with Google
      </Button>
      <Button
        fullWidth
        variant="outlined"
        onClick={() => alert('Sign in with Facebook')}
        startIcon={<FacebookIcon />}
      >
        Sign in with Facebook
      </Button>
    </>
  );

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <SignInCard variant="outlined">
          <SitemarkIcon />
          <Typography component="h1" variant="h4">
            Sign in
          </Typography>
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                autoComplete="email"
                fullWidth
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                id="password"
                name="password"
                type="password"
                placeholder="••••••"
                autoComplete="current-password"
                fullWidth
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
              />
            </FormControl>
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
            <ForgotPassword
              email={formik.values.email}
              open={openForgotPassword}
              handleClose={handleClose}
            />
            <Button type="submit" fullWidth variant="contained">
              Sign in
            </Button>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              Forgot your password?
            </Link>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SocialLogins}
              <Typography sx={{ textAlign: 'center' }}>
                Don&apos;t have an account?{' '}
                <Link
                  href="/auth/register"
                  variant="body2"
                  sx={{ alignSelf: 'center' }}
                >
                  Register
                </Link>
              </Typography>
            </Box>
          </Box>
        </SignInCard>
      </SignInContainer>
    </AppTheme>
  );
}

export default SignIn;
